import { useState, useReducer, useCallback, useEffect } from 'react';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent } from '@exam-prep/ui';
import { api, ApiError } from '../../lib/api';
import { ErrorBox, Loading } from '../../components/Feedback';
import type { CardsHome } from './model/cards';
import { reduce, type Session, type CardFace, cardsError, newRequestKey } from './model/flow';
import { useExamProtection } from '../exam-guard';
import { CardView, RatingBar } from './ui/Cards';

/**
 * Flashcards (ADR 0037): what is due, then a one-card-at-a-time review session. The server decides
 * which cards this learner may see; free learners get the fixed sampler and see locked decks.
 */
export function Cards({ onBack }: { onBack: () => void }) {
  const [home, setHome] = useState<CardsHome | null>(null);
  const [homeError, setHomeError] = useState<unknown>(null);
  const [reviewing, setReviewing] = useState(false);
  const [s, dispatch] = useReducer(reduce, { kind: 'loading' } as Session);
  const notice = useExamProtection(reviewing);

  const loadHome = useCallback(() => {
    api<CardsHome>('GET', '/app/cards').then(setHome, setHomeError);
  }, []);
  useEffect(loadHome, [loadHome]);

  const loadNext = useCallback(() => {
    api<{ card: CardFace | null }>('GET', '/app/cards/next').then(
      (r) => dispatch({ type: 'loaded', card: r.card }),
      (e: unknown) =>
        dispatch({ type: 'failed', message: cardsError(e instanceof ApiError ? e.code : '') }),
    );
  }, []);

  useEffect(() => {
    if (reviewing && s.kind === 'loading') loadNext();
  }, [reviewing, s.kind, loadNext]);

  useEffect(() => {
    if (s.kind !== 'saving') return;
    api('POST', '/app/cards/rate', {
      cardId: s.card.id,
      rating: s.rating,
      requestKey: s.requestKey,
    }).then(
      () => dispatch({ type: 'saved' }),
      (e: unknown) =>
        dispatch({ type: 'failed', message: cardsError(e instanceof ApiError ? e.code : '') }),
    );
  }, [s]);

  if (homeError) return <ErrorBox error={homeError} />;
  if (!home) return <Loading />;

  if (reviewing)
    return (
      <div className="exam-protected space-y-4 p-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              setReviewing(false);
              loadHome();
            }}
          >
            ← Done
          </Button>
          <span className="text-sm text-muted-foreground">{s.done ?? 0} reviewed</span>
        </div>
        {notice ? <p className="rounded bg-muted p-2 text-center text-xs">{notice}</p> : null}
        {s.kind === 'loading' ? <Loading /> : null}
        {s.kind === 'front' || s.kind === 'back' || s.kind === 'saving' ? (
          <>
            <CardView
              card={s.card}
              flipped={s.kind !== 'front'}
              onFlip={() => dispatch({ type: 'flip' })}
            />
            {s.kind === 'front' ? (
              <Button className="w-full" size="lg" onClick={() => dispatch({ type: 'flip' })}>
                Show answer
              </Button>
            ) : (
              <RatingBar
                disabled={s.kind === 'saving'}
                onRate={(rating) => dispatch({ type: 'rate', rating, requestKey: newRequestKey() })}
              />
            )}
          </>
        ) : null}
        {s.kind === 'finished' ? (
          <Card>
            <CardHeader>
              <CardTitle>All caught up</CardTitle>
              <CardDescription>
                Nothing more to review right now. Come back later for the next cards.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : null}
        {s.kind === 'error' ? (
          <p className="rounded-2xl border bg-card p-4 text-center text-sm">{s.message}</p>
        ) : null}
      </div>
    );

  const canStudy = home.due > 0 || home.newLeft > 0;
  const nothingYet = home.full ? home.decks.every((d) => d.cards === 0) : home.samplerSize === 0;
  if (nothingYet)
    return (
      <div className="space-y-4 p-4">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <p className="rounded-2xl border bg-card p-6 text-center text-sm">
          No flashcards for your exam yet.
        </p>
      </div>
    );
  return (
    <div className="space-y-4 p-4">
      <Button variant="ghost" onClick={onBack}>
        ← Back
      </Button>
      <h1 className="font-display text-2xl font-bold">Flashcards</h1>
      <Card>
        <CardHeader>
          <CardTitle>Due today ({home.due})</CardTitle>
          <CardDescription>
            {home.full
              ? `You can start ${String(home.newLeft)} new cards today.`
              : `Free plan: a ${String(home.samplerSize)}-card sampler. Every deck comes with Monthly, Till Exam and Annual plans (/upgrade in the bot).`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            className="w-full"
            size="lg"
            disabled={!canStudy}
            onClick={() => setReviewing(true)}
          >
            Review now
          </Button>
        </CardContent>
      </Card>
      <ul className="space-y-2" aria-label="Decks">
        {home.decks.map((d) => (
          <li key={d.id} className="flex items-center justify-between rounded-xl border p-3">
            <span>
              <span className="font-medium">{d.title}</span>
              <span className="block text-xs text-muted-foreground">
                {d.subject} · {d.cards} cards
              </span>
            </span>
            {d.locked ? <span aria-label="Locked">🔒</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
